import React, {
  createContext,
  FC,
  ReactNode,
  useContext,
  useMemo,
} from 'react';

import { useGraphData as useGraphDataHook } from '@/components/nodeGraph/hooks/data/useGraphData';
import { Filters } from '@/hooks/useAssets';
import { Asset, Attribute, Risk, Seed, Technology, Webpage } from '@/types';

import { useGraphStateContext } from './GraphStateProvider';

/**
 * Consolidated graph structure mapping node IDs to their data
 */
export interface ConsolidatedGraph {
  [key: string]: {
    type: string;
    data: Asset | Risk | Technology | Attribute | Seed | Webpage;
  };
}

/**
 * GraphDataContext - API Data and Pagination Management
 *
 * This context manages all data fetched from the backend API including:
 * - Entity arrays (assets, risks, technologies, seeds)
 * - Consolidated graph structure for visualization
 * - Loading states for progressive data loading
 * - Pagination controls for infinite scrolling
 * - Max nodes configuration
 *
 * Features:
 * - Progressive data loading (assets first, then relationships)
 * - Infinite pagination support
 * - Filter management
 * - Configurable node limits
 */
interface GraphDataContextValue {
  // Entities
  assets: Asset[];
  risks: Risk[];
  technologies: Technology[];
  seeds: Seed[];

  // Graph structure
  consolidatedGraph: ConsolidatedGraph;

  // Loading states
  isLoading: boolean;
  isLoadingRelatedData: boolean;

  // Pagination
  hasNextPage: boolean;
  fetchNextPage: () => void;

  // Filters
  assetFilters: Filters;
  setAssetFilters: (filters: Filters | ((prev: Filters) => Filters)) => void;

  // Configuration
  maxNodes: number;
  setMaxNodes: (maxNodes: number) => void;
}

const GraphDataContext = createContext<GraphDataContextValue | null>(null);

export interface GraphDataProviderProps {
  filters?: Filters;
  children: ReactNode;
}

/**
 * GraphDataProvider - Provides API data to the graph component tree
 *
 * This provider wraps the useGraphData hook and makes its data available
 * throughout the component tree via context. It manages:
 *
 * 1. Entity Data: Assets, risks, technologies, seeds
 * 2. Graph Structure: Consolidated graph for visualization
 * 3. Loading States: Progressive loading indicators
 * 4. Pagination: Infinite scroll support
 * 5. Configuration: Max nodes and filters
 *
 * Dependencies:
 * - GraphStateProvider: Uses entity type for filtering
 * - useGraphData hook: Fetches and consolidates data
 *
 * Usage:
 * ```tsx
 * <GraphDataProvider filters={filters}>
 *   <GraphComponents />
 * </GraphDataProvider>
 * ```
 *
 * Access data via:
 * ```tsx
 * const { assets, isLoading, fetchNextPage } = useGraphDataContext();
 * ```
 */
export const GraphDataProvider: FC<GraphDataProviderProps> = ({
  filters,
  children,
}) => {
  // Get entity type and max nodes from state context
  const { entity, config } = useGraphStateContext();

  // Fetch all graph data using the composition hook
  const graphData = useGraphDataHook(entity.type, filters, config.maxNodes);

  // Memoize context value to prevent unnecessary re-renders
  const value = useMemo<GraphDataContextValue>(
    () => ({
      // Entity arrays
      assets: graphData.assets,
      risks: graphData.risks,
      technologies: graphData.technologies,
      seeds: graphData.seeds,

      // Graph structure
      consolidatedGraph: graphData.consolidatedGraph,

      // Loading states
      isLoading: graphData.isLoading,
      isLoadingRelatedData: graphData.isLoadingRelatedData,

      // Pagination
      hasNextPage: graphData.hasNextPage,
      fetchNextPage: graphData.fetchNextPage,

      // Filters
      assetFilters: graphData.assetFilters,
      setAssetFilters: graphData.setAssetFilters,

      // Configuration
      maxNodes: config.maxNodes,
      setMaxNodes: config.setMaxNodes,
    }),
    [graphData, config.maxNodes, config.setMaxNodes]
  );

  return (
    <GraphDataContext.Provider value={value}>
      {children}
    </GraphDataContext.Provider>
  );
};

/**
 * useGraphDataContext - Access graph data from context
 *
 * This hook provides access to all API data and pagination controls.
 * Must be used within a GraphDataProvider.
 *
 * @throws Error if used outside GraphDataProvider
 * @returns GraphDataContextValue containing all graph data and controls
 *
 * @example
 * ```tsx
 * const { assets, isLoading, fetchNextPage } = useGraphDataContext();
 *
 * if (isLoading) return <Loader />;
 *
 * return (
 *   <div>
 *     {assets.map(asset => <AssetNode key={asset.key} asset={asset} />)}
 *     {hasNextPage && <button onClick={fetchNextPage}>Load More</button>}
 *   </div>
 * );
 * ```
 */
export const useGraphDataContext = (): GraphDataContextValue => {
  const context = useContext(GraphDataContext);
  if (!context) {
    throw new Error(
      'useGraphDataContext must be used within GraphDataProvider'
    );
  }
  return context;
};
